import React, { useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { SiteHeader } from "../components/SiteHeader";
import { motion } from "framer-motion";
import { Wind, Armchair, Waves, Download, Phone, ArrowRight, Check } from "lucide-react";

const BRACED_CALL = "https://scheduler.zoom.us/casey-muze/braced-call";

const LEDGER = [
  {
    key: "jaw",
    icon: Wind,
    label: "The Jaw",
    tag: "The Vagal Gate",
    body: "Often the first site of clamping down to hold words or feelings in. When the jaw softens, the whole system hears that it no longer has to brace so hard.",
  },
  {
    key: "shoulders",
    icon: Armchair,
    label: "The Shoulders",
    tag: "The Armor",
    body: "Muscles that lift toward the ears as if bracing for impact — readiness, over-responsibility, constant vigilance. The clearest cue the body hasn't been given permission to rest.",
  },
  {
    key: "breath",
    icon: Waves,
    label: "The Diaphragm",
    tag: "The Core Seat",
    body: "Where the body holds its breath to keep composure under pressure. Softening the breath shifts the nervous system from protection toward steadiness.",
  },
];

const RESETS = [
  {
    n: "1",
    title: "Jaw & Tongue Release",
    body: "Part the teeth slightly, let the tongue drop from the roof of the mouth, and exhale without forcing.",
    cue: "Notice whether the face, throat or chest softens with it.",
  },
  {
    n: "2",
    title: "Seated Chair Grounding",
    body: "Let the weight of your body be fully held by the chair. Let the pelvis get heavy and feel the seat meet your sit-bones.",
    cue: "You don't have to hold yourself up with tension when support is already here.",
  },
  {
    n: "3",
    title: "The 8-Count Vagal Brake",
    body: "Inhale slowly, then stretch the exhale for a full count of eight to signal safety to the brain.",
    cue: "1 · 2 · 3 · 4 · 5 · 6 · 7 · 8 — gentle, not performative. The goal is ease, not effort.",
  },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const triggerDownload = () => {
  const a = document.createElement("a");
  a.href = `${API}/somatic-reset/guide.pdf`;
  a.download = "braced-no-more-1-minute-somatic-reset.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export default function SomaticReset() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [zone, setZone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) { setError("Please enter a valid email."); return; }
    setStatus("sending");
    try {
      await axios.post(`${API}/somatic-reset/lead`, {
        email: email.trim(),
        first_name: firstName.trim(),
        hold_zone: zone,
        origin_url: window.location.href,
      });
      triggerDownload();
      setStatus("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again.");
      setStatus("idle");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100 text-ink" data-testid="somatic-reset-page">
      <SiteHeader theme="light" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-24 top-[-10%] h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-[-20%] h-72 w-72 rounded-full bg-sage/10 blur-3xl" />
        <div className="mx-auto grid max-w-5xl gap-8 px-6 py-16 sm:px-10 sm:py-24 lg:grid-cols-[1.3fr_1fr] lg:items-center">
          <motion.div {...fade(0)}>
            <p className="kicker text-gold-foil">Free · A body-first reset</p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              The 1-Minute<br />Somatic Reset
            </h1>
            <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-ink-soft">
              You've held it together long enough. This gentle, body-first guide helps you notice where
              you're bracing, soften what you can, and find your way back to yourself — one minute at a time.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href="#get-the-guide" data-testid="hero-get-guide"
                className="debossed-btn inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-ink">
                Send me the pocket guide <ArrowRight className="h-4 w-4" />
              </a>
              <a href={BRACED_CALL} target="_blank" rel="noopener noreferrer" data-testid="hero-braced-call"
                className="inline-flex items-center gap-2 font-display text-sm text-gold-deep underline-offset-4 transition hover:gap-3 hover:underline">
                <Phone strokeWidth={1.5} className="h-4 w-4" /> Book a 60-minute Braced Call
              </a>
            </div>
          </motion.div>

          <motion.div {...fade(0.15)} className="relative">
            <div className="rounded-2xl bg-ivory-50 p-7 ring-1 ring-ivory-400/60 shadow-[0_30px_80px_-50px_rgba(60,46,30,0.55)]">
              <p className="kicker text-sage-deep">What is your body trying to say?</p>
              <p className="mt-3 font-display text-xl leading-snug text-ink">
                A quiet 60-second check-in for the capable, the caregiving, the quietly depleted.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-soft">
                You are not broken. You are just braced. Your body didn't betray you — it stood guard
                when you had to stay strong.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Muscle Ledger */}
      <section className="mx-auto max-w-5xl px-6 py-14 sm:px-10">
        <motion.div {...fade(0)}>
          <p className="kicker text-gold-foil">The Muscle Ledger</p>
          <h2 className="mt-2 font-display text-3xl text-ink sm:text-4xl">Where you hold it.</h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-soft">
            Your body has a story to tell, and you don't have to rush it. Explore these common places of
            tension with curiosity, not judgment.
          </p>
        </motion.div>
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {LEDGER.map((z, i) => (
            <motion.div key={z.key} {...fade(i * 0.08)} data-testid={`ledger-${z.key}`}
              className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/60 transition hover:ring-gold/40">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gold/10 text-gold-deep">
                <z.icon strokeWidth={1.5} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-xl text-ink">{z.label}</h3>
              <p className="text-xs uppercase tracking-[0.18em] text-sage-deep">{z.tag}</p>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">{z.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Micro-resets */}
      <section className="bg-ivory-200/60">
        <div className="mx-auto max-w-5xl px-6 py-14 sm:px-10">
          <motion.div {...fade(0)}>
            <p className="kicker text-gold-foil">The 1-Minute Micro-Resets</p>
            <h2 className="mt-2 font-display text-3xl text-ink sm:text-4xl">No perfect practice required.</h2>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-soft">
              Small, gentle resets that can meet you where you are — at your desk, in the car, or between meetings.
            </p>
          </motion.div>
          <div className="mt-10 space-y-4">
            {RESETS.map((r, i) => (
              <motion.div key={r.n} {...fade(i * 0.08)} data-testid={`reset-${r.n}`}
                className="flex gap-5 rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/60">
                <span className="flex-none font-display text-3xl text-gold-deep/70">{r.n}</span>
                <div>
                  <h3 className="font-display text-xl text-ink">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{r.body}</p>
                  <p className="mt-2 text-sm italic leading-relaxed text-sage-deep">{r.cue}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead capture / confirmation */}
      <section id="get-the-guide" className="mx-auto max-w-2xl scroll-mt-24 px-6 py-16 sm:px-10" data-testid="get-the-guide">
        {status === "done" ? (
          <motion.div {...fade(0)} className="rounded-3xl bg-ivory-50 p-8 text-center ring-1 ring-gold/30 shadow-[0_30px_80px_-50px_rgba(60,46,30,0.55)]" data-testid="reset-confirmation">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-sage/15 text-sage-deep">
              <Check strokeWidth={1.6} className="h-7 w-7" />
            </span>
            <h2 className="mt-5 font-display text-3xl text-ink">You are on the map.</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              Your 1-Minute Somatic Reset Pocket Guide is downloading now, and a copy is on its way to
              your inbox. Before you rush on, take one slow breath — unclench the jaw a little, let the
              tongue rest, drop the shoulders half an inch.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
              <button onClick={triggerDownload} data-testid="reset-redownload"
                className="debossed-btn inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-ink">
                <Download className="h-4 w-4" /> Download again
              </button>
              <a href={BRACED_CALL} target="_blank" rel="noopener noreferrer" data-testid="confirm-braced-call"
                className="inline-flex items-center gap-2 font-display text-sm text-gold-deep underline-offset-4 transition hover:gap-3 hover:underline">
                <Phone strokeWidth={1.5} className="h-4 w-4" /> Book a 60-minute Braced Call
              </a>
            </div>
          </motion.div>
        ) : (
          <motion.div {...fade(0)} className="rounded-3xl bg-ivory-50 p-8 ring-1 ring-ivory-400/60 shadow-[0_30px_80px_-50px_rgba(60,46,30,0.55)]">
            <p className="kicker text-gold-foil">Free · Instant download</p>
            <h2 className="mt-2 font-display text-3xl text-ink">Get the guide and begin your check-in.</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              Choose where you hold stress most often and enter your email — your Pocket Guide (PDF)
              downloads instantly, and a copy lands in your inbox too.
            </p>
            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-ink-muted">Where do you brace most?</p>
                <div className="flex flex-wrap gap-2" data-testid="zone-picker">
                  {LEDGER.map((z) => (
                    <button type="button" key={z.key} onClick={() => setZone(z.key)}
                      data-testid={`zone-${z.key}`}
                      className={`rounded-full border px-4 py-2 text-sm transition ${zone === z.key
                        ? "border-gold bg-gold/10 text-gold-deep"
                        : "border-ink/15 text-ink-soft hover:border-gold/50"}`}>
                      {z.label}
                    </button>
                  ))}
                </div>
              </div>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)" data-testid="reset-firstname"
                className="w-full rounded-xl border border-ink/15 bg-ivory-50 px-4 py-3 text-ink outline-none transition focus:border-gold/60" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email" required data-testid="reset-email"
                className="w-full rounded-xl border border-ink/15 bg-ivory-50 px-4 py-3 text-ink outline-none transition focus:border-gold/60" />
              {error && <p className="text-sm text-terracotta-deep" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={status === "sending"} data-testid="reset-submit"
                className="debossed-btn inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink disabled:opacity-60">
                <Download className="h-4 w-4" />
                {status === "sending" ? "Preparing your guide…" : "Send me the pocket guide"}
              </button>
              <p className="text-center text-xs text-ink-muted">
                Grounded in trauma-informed somatic safety. Your PDF downloads instantly.
              </p>
            </form>
          </motion.div>
        )}
      </section>
    </div>
  );
}
